import { Sun, Moon, Palette, RotateCcw, Check } from 'lucide-react';
import { useAdminTheme, ACCENT_SWATCHES } from '@/lib/adminTheme';
import { useLanguage } from '@/lib/i18n';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

/**
 * Compact admin-only theme switcher.
 * Drops into the dashboard topbar. Subtle, professional, no surprises.
 */
export default function AdminThemeSwitcher() {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const { mode, accent, accents, setMode, setAccent, reset } = useAdminTheme();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="admin-theme-switcher"
          aria-label={ja ? '表示テーマ' : 'Display theme'}
          className="hidden md:flex items-center gap-1.5 px-2.5 py-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors text-sm font-medium"
          title={ja ? '管理画面の表示を変更' : 'Customize admin appearance'}
        >
          <Palette className="w-[18px] h-[18px]" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-4 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
        data-testid="admin-theme-panel"
      >
        <div className="space-y-4">
          <div>
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
              {ja ? '表示モード' : 'Mode'}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMode('light')}
                data-testid="admin-theme-mode-light"
                className={`flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-colors border ${
                  mode === 'light'
                    ? 'bg-amber-50 border-amber-300 text-amber-700'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400'
                }`}
              >
                <Sun className="w-3.5 h-3.5" />
                {ja ? 'ライト' : 'Light'}
              </button>
              <button
                onClick={() => setMode('dark')}
                data-testid="admin-theme-mode-dark"
                className={`flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold transition-colors border ${
                  mode === 'dark'
                    ? 'bg-slate-800 border-slate-600 text-slate-100'
                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-400'
                }`}
              >
                <Moon className="w-3.5 h-3.5" />
                {ja ? 'ダーク' : 'Dark'}
              </button>
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
              {ja ? 'アクセントカラー' : 'Accent colour'}
            </div>
            <div className="grid grid-cols-6 gap-2">
              {accents.map((a) => {
                const sw = ACCENT_SWATCHES[a];
                const active = accent === a;
                return (
                  <button
                    key={a}
                    onClick={() => setAccent(a)}
                    data-icon-only
                    data-testid={`admin-theme-accent-${a}`}
                    aria-label={ja ? sw.ja : sw.en}
                    title={ja ? sw.ja : sw.en}
                    className={`relative w-8 h-8 rounded-full transition-transform hover:scale-110 ${active ? 'ring-2 ring-offset-2 ring-slate-400 dark:ring-offset-slate-900' : ''}`}
                    style={{ backgroundColor: sw.hex }}
                  >
                    {active && <Check className="w-3.5 h-3.5 text-white absolute inset-0 m-auto" strokeWidth={3} />}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="pt-2 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between text-[11px]">
            <span className="text-slate-500 dark:text-slate-400">
              {ja ? '管理者ごとに保存' : 'Saved per admin'}
            </span>
            <button
              onClick={reset}
              data-testid="admin-theme-reset"
              className="flex items-center gap-1 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
            >
              <RotateCcw className="w-3 h-3" />
              {ja ? 'リセット' : 'Reset'}
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
